import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { messagingAPI, channelsAPI, usersAPI, vansAPI, aiActionsAPI, aiProposalsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { MentionComposer } from '../components/MentionComposer';
import { ChannelCreateModal } from '../components/ChannelCreateModal';
import { ChannelSettingsDrawer } from '../components/ChannelSettingsDrawer';
import { AIActionCard } from '../components/AIActionCard';
import { AIUsageDashboard } from '../components/AIUsageDashboard';
import {
  MessageSquare, Loader2, Send, User, Users, Truck, Plus, ArrowLeft,
  Trash2, Clock, Hash, Lock, ImagePlus, Briefcase, Users2, FileText, Receipt,
  Settings, Bell, BellOff, BellRing, Volume2, X, Upload, Package, ClipboardList,
  SmilePlus, Bot, Zap, AlertTriangle,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const formatLocalTime = (utcTimestamp, formatStr) => {
  if (!utcTimestamp) return '';
  try {
    let date;
    if (utcTimestamp.endsWith('Z')) date = parseISO(utcTimestamp);
    else if (utcTimestamp.includes('+') || utcTimestamp.includes('-', 10)) date = parseISO(utcTimestamp);
    else date = parseISO(utcTimestamp + 'Z');
    return format(date, formatStr);
  } catch { return format(new Date(utcTimestamp), formatStr); }
};

// Mention token regex: {{mention:type:id:display}}
const MENTION_RE = /\{\{mention:(\w+):([^:]+):([^}]*)\}\}/g;

const ENTITY_ROUTES = {
  user: (id) => null, // no navigation for user mentions
  job: (id) => `/jobs/${id}`,
  customer: (id) => `/customers/${id}`,
  estimate: (id) => `/estimates/${id}`,
  invoice: (id) => `/invoices/${id}`,
  part: (id) => `/catalogue?highlight=${id}`,
  vehicle: (id) => `/vans/${id}`,
  po: (id) => `/purchase-orders?highlight=${id}`,
};

const MENTION_ICONS = {
  user: User,
  job: Briefcase,
  customer: Users2,
  estimate: FileText,
  invoice: Receipt,
  part: Package,
  vehicle: Truck,
  po: ClipboardList,
};

function RenderMessageBody({ bodyRaw, mentions, navigate, isOwn }) {
  // Build rendered content with clickable mention pills
  if (!bodyRaw) return null;

  const parts = [];
  let lastIndex = 0;
  const mentionMap = {};
  (mentions || []).forEach(m => { mentionMap[`${m.mention_type}:${m.entity_id}`] = m; });

  let match;
  const regex = new RegExp(MENTION_RE.source, 'g');
  while ((match = regex.exec(bodyRaw)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={lastIndex}>{bodyRaw.slice(lastIndex, match.index)}</span>);
    }
    const [, type, entityId, display] = match;
    const route = ENTITY_ROUTES[type]?.(entityId);
    const Icon = MENTION_ICONS[type] || User;
    // Pill styling: high contrast in both outgoing (blue bg) and incoming (gray bg) bubbles
    const pillClass = isOwn
      ? 'bg-white/25 text-white border border-white/30 hover:bg-white/35'
      : 'bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200';
    parts.push(
      <span
        key={match.index}
        className={`mention-pill inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold ${pillClass} ${route ? 'cursor-pointer' : ''} transition-colors`}
        onClick={route ? (e) => { e.stopPropagation(); navigate(route); } : undefined}
        data-testid={`mention-pill-${type}-${entityId}`}
      >
        <Icon className="w-3 h-3 shrink-0" />
        @{display}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < bodyRaw.length) {
    parts.push(<span key={lastIndex}>{bodyRaw.slice(lastIndex)}</span>);
  }
  return <>{parts}</>;
}


function ImageAttachment({ att }) {
  const [imgSrc, setImgSrc] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const loadImage = async () => {
      try {
        const res = await channelsAPI.serveAttachmentBlob(att.id);
        if (!cancelled) {
          const url = URL.createObjectURL(new Blob([res.data], { type: att.mime_type }));
          setImgSrc(url);
        }
      } catch { /* ignore */ }
    };
    loadImage();
    return () => { cancelled = true; };
  }, [att.id, att.mime_type]);

  if (!imgSrc) return <div className="mt-2 w-40 h-24 bg-muted rounded-lg animate-pulse" />;
  return (
    <a href={imgSrc} target="_blank" rel="noopener noreferrer" className="block mt-2">
      <img
        src={imgSrc}
        alt={att.original_filename}
        className="rounded-lg max-w-xs max-h-60 object-cover border"
        loading="lazy"
        data-testid={`attachment-${att.id}`}
      />
    </a>
  );
}

const EMOJI_CATALOG = [
  { id: 'common', label: 'Common', emojis: [
    { e: '👍', kw: ['thumbs up', 'yes', 'good', 'ok', 'agree', 'like'] },
    { e: '👎', kw: ['thumbs down', 'no', 'bad', 'disagree', 'dislike'] },
    { e: '❤️', kw: ['heart', 'love', 'like', 'favorite'] },
    { e: '😂', kw: ['laugh', 'lol', 'funny', 'haha'] },
    { e: '😄', kw: ['smile', 'happy', 'grin'] },
    { e: '🔥', kw: ['fire', 'hot', 'lit', 'awesome'] },
    { e: '👀', kw: ['eyes', 'look', 'looking', 'watching', 'seen'] },
  ]},
  { id: 'approval', label: 'Approval / Response', emojis: [
    { e: '✅', kw: ['check', 'done', 'complete', 'yes', 'approved'] },
    { e: '❌', kw: ['cross', 'no', 'wrong', 'rejected', 'cancel'] },
    { e: '🙌', kw: ['raise', 'hands', 'praise', 'hooray', 'yay'] },
    { e: '🚫', kw: ['ban', 'stop', 'prohibited', 'blocked'] },
    { e: '⚠️', kw: ['warning', 'alert', 'caution', 'attention'] },
    { e: '👏', kw: ['clap', 'applause', 'bravo', 'nice'] },
    { e: '💯', kw: ['hundred', 'perfect', 'score', 'exactly'] },
  ]},
  { id: 'work', label: 'Work / Status', emojis: [
    { e: '📌', kw: ['pin', 'pinned', 'important', 'bookmark'] },
    { e: '🛠️', kw: ['tools', 'wrench', 'fix', 'repair', 'build'] },
    { e: '⏳', kw: ['hourglass', 'waiting', 'time', 'pending'] },
    { e: '🔒', kw: ['lock', 'locked', 'secure', 'private'] },
    { e: '📝', kw: ['note', 'memo', 'write', 'edit', 'document'] },
    { e: '💡', kw: ['idea', 'bulb', 'light', 'tip', 'suggestion'] },
    { e: '🎯', kw: ['target', 'goal', 'bullseye', 'focus'] },
    { e: '📊', kw: ['chart', 'graph', 'data', 'stats', 'analytics'] },
  ]},
  { id: 'celebration', label: 'Celebration / Positive', emojis: [
    { e: '🎉', kw: ['party', 'celebrate', 'confetti', 'congrats'] },
    { e: '💪', kw: ['strong', 'muscle', 'flex', 'power'] },
    { e: '🌟', kw: ['star', 'glowing', 'sparkle', 'excellent'] },
    { e: '🥳', kw: ['party', 'celebrate', 'birthday', 'woohoo'] },
    { e: '💚', kw: ['green heart', 'love', 'go', 'approved'] },
    { e: '⭐', kw: ['star', 'favorite', 'rating', 'gold'] },
    { e: '🏆', kw: ['trophy', 'winner', 'champion', 'award'] },
  ]},
  { id: 'thinking', label: 'Questions / Thinking', emojis: [
    { e: '🤔', kw: ['thinking', 'hmm', 'consider', 'wonder'] },
    { e: '😕', kw: ['confused', 'unsure', 'puzzled'] },
    { e: '❓', kw: ['question', 'what', 'why', 'how'] },
    { e: '💭', kw: ['thought', 'bubble', 'thinking', 'idea'] },
    { e: '😬', kw: ['grimace', 'awkward', 'oops', 'yikes'] },
  ]},
];

// Flat set for quick validation
const ALL_EMOJIS_SET = new Set(EMOJI_CATALOG.flatMap(c => c.emojis.map(e => e.e)));

function ReactionPills({ reactions, onToggle, msgId }) {
  if (!reactions || reactions.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={`reactions-${msgId}`}>
      {reactions.map(r => (
        <button
          key={r.emoji}
          onClick={() => onToggle(r.emoji)}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
            r.reacted
              ? 'bg-primary/10 border-primary/30 text-primary font-semibold'
              : 'bg-muted/50 border-border text-muted-foreground hover:bg-muted'
          }`}
          data-testid={`reaction-pill-${msgId}-${r.emoji}`}
        >
          <span>{r.emoji}</span>
          <span>{r.count}</span>
        </button>
      ))}
    </div>
  );
}

// Flat keyword index for search
const EMOJI_SEARCH_INDEX = EMOJI_CATALOG.flatMap(c =>
  c.emojis.map(({ e, kw }) => ({ emoji: e, keywords: kw.join(' '), category: c.label }))
);

// Recent reactions — localStorage per user
const RECENT_KEY_PREFIX = 'reactions_recent_';
const MAX_RECENT = 12;

function getRecentEmojis(userId) {
  try {
    const raw = localStorage.getItem(RECENT_KEY_PREFIX + userId);
    return raw ? JSON.parse(raw).filter(e => ALL_EMOJIS_SET.has(e)) : [];
  } catch { return []; }
}

function pushRecentEmoji(userId, emoji) {
  const list = getRecentEmojis(userId);
  const updated = [emoji, ...list.filter(e => e !== emoji)].slice(0, MAX_RECENT);
  try { localStorage.setItem(RECENT_KEY_PREFIX + userId, JSON.stringify(updated)); } catch { /* ignore */ }
  return updated;
}

function EmojiPicker({ onSelect, msgId, userId }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('recent');
  const [recents, setRecents] = React.useState([]);
  const ref = React.useRef(null);
  const searchRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      setRecents(getRecentEmojis(userId));
      setSearch('');
      setActiveTab('recent');
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open, userId]);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (emoji) => {
    const updated = pushRecentEmoji(userId, emoji);
    setRecents(updated);
    onSelect(emoji);
    setOpen(false);
  };

  const lowerSearch = search.toLowerCase().trim();
  const filtered = lowerSearch
    ? EMOJI_SEARCH_INDEX.filter(e => e.emoji.includes(lowerSearch) || e.keywords.includes(lowerSearch))
    : null;

  // Category tabs
  const tabs = [
    { id: 'recent', label: '🕐' },
    ...EMOJI_CATALOG.map(c => ({ id: c.id, label: c.label.split(' ')[0] })),
  ];

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="text-muted-foreground/50 hover:text-muted-foreground transition-colors p-0.5"
        data-testid={`emoji-picker-btn-${msgId}`}
        title="Add reaction"
      >
        <SmilePlus className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute bottom-full mb-1 right-0 bg-popover border rounded-xl shadow-xl z-50 w-[280px]"
          data-testid={`emoji-picker-popup-${msgId}`}
        >
          {/* Search */}
          <div className="p-2 pb-0">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search reactions..."
              className="w-full px-2.5 py-1.5 text-xs border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
              data-testid="emoji-search-input"
            />
          </div>

          {/* Search results */}
          {filtered ? (
            <div className="p-2 max-h-[180px] overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">No matches</p>
              ) : (
                <div className="grid grid-cols-7 gap-0.5">
                  {filtered.map(({ emoji }) => (
                    <button
                      key={emoji}
                      onClick={() => handleSelect(emoji)}
                      className="w-8 h-8 rounded-md hover:bg-muted flex items-center justify-center text-base transition-colors"
                      data-testid={`emoji-option-${emoji}`}
                      title={EMOJI_SEARCH_INDEX.find(e => e.emoji === emoji)?.keywords}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Category tabs */}
              <div className="flex border-b px-1 pt-1 gap-0.5 overflow-x-auto scrollbar-none">
                {tabs.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`px-2 py-1 text-[10px] font-medium rounded-t-md whitespace-nowrap transition-colors ${
                      activeTab === t.id
                        ? 'bg-muted text-foreground border-b-2 border-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`emoji-tab-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Emoji grid */}
              <div className="p-2 max-h-[180px] overflow-y-auto">
                {activeTab === 'recent' ? (
                  recents.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-3">No recent reactions yet</p>
                  ) : (
                    <div className="grid grid-cols-7 gap-0.5">
                      {recents.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleSelect(emoji)}
                          className="w-8 h-8 rounded-md hover:bg-muted flex items-center justify-center text-base transition-colors"
                          data-testid={`emoji-option-${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  (() => {
                    const cat = EMOJI_CATALOG.find(c => c.id === activeTab);
                    if (!cat) return null;
                    return (
                      <>
                        <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-1.5">{cat.label}</p>
                        <div className="grid grid-cols-7 gap-0.5">
                          {cat.emojis.map(({ e }) => (
                            <button
                              key={e}
                              onClick={() => handleSelect(e)}
                              className="w-8 h-8 rounded-md hover:bg-muted flex items-center justify-center text-base transition-colors"
                              data-testid={`emoji-option-${e}`}
                            >
                              {e}
                            </button>
                          ))}
                        </div>
                      </>
                    );
                  })()
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


function ChannelSidebarItem({ ch, selected, unread, onClick }) {
  const isMuted = ch.notify_mode === 'muted';
  const isMentionsOnly = ch.notify_mode === 'mentions';
  return (
    <div
      className={`px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 ${
        selected ? 'bg-muted' : ''
      } ${unread > 0 ? 'bg-primary/5' : ''}`}
      onClick={onClick}
      data-testid={`channel-item-${ch.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0 relative">
          {ch.is_private ? <Lock className="w-4 h-4 text-primary" /> : <Hash className="w-4 h-4 text-primary" />}
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-destructive text-[10px] font-bold text-white rounded-full flex items-center justify-center px-1">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm truncate ${unread > 0 ? 'font-bold' : 'font-semibold'} ${isMuted ? 'text-muted-foreground' : ''}`}>{ch.name}</p>
          {ch.description && <p className="text-xs text-muted-foreground truncate">{ch.description}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isMuted && <BellOff className="w-3 h-3 text-muted-foreground" />}
          {isMentionsOnly && <Bell className="w-3 h-3 text-amber-500" />}
          <span className="text-[10px] text-muted-foreground">{ch.member_count}m</span>
        </div>
      </div>
    </div>
  );
}

function ChannelSidebarList({ channels, selectedChannel, channelUnreadByChannel, onSelect, onJoin }) {
  const joined = channels.filter(ch => ch.is_member);
  const available = channels.filter(ch => !ch.is_member && !ch.is_private);

  return (
    <div>
      {/* Joined channels */}
      {joined.length > 0 && (
        <div>
          {available.length > 0 && (
            <div className="px-4 pt-3 pb-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Your Channels</p>
            </div>
          )}
          <div className="divide-y">
            {joined.map(ch => (
              <ChannelSidebarItem
                key={ch.id}
                ch={ch}
                selected={selectedChannel?.id === ch.id}
                unread={channelUnreadByChannel[ch.id] || 0}
                onClick={() => onSelect(ch)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available public channels the user hasn't joined */}
      {available.length > 0 && (
        <div>
          <div className="px-4 pt-3 pb-1 border-t">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Available to Join</p>
          </div>
          <div className="divide-y">
            {available.map(ch => (
              <div
                key={ch.id}
                className={`px-4 py-3 transition-colors hover:bg-muted/50 ${
                  selectedChannel?.id === ch.id ? 'bg-muted' : ''
                }`}
                data-testid={`channel-item-${ch.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <Hash className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onSelect(ch)}>
                    <p className="text-sm truncate font-medium text-muted-foreground">{ch.name}</p>
                    {ch.description && <p className="text-xs text-muted-foreground/70 truncate">{ch.description}</p>}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 text-xs h-7 px-2"
                    onClick={(e) => { e.stopPropagation(); onJoin(ch); }}
                    data-testid={`join-channel-${ch.id}`}
                  >
                    Join
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {joined.length === 0 && available.length === 0 && (
        <div className="px-4 py-8 text-center">
          <Hash className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No channels yet</p>
        </div>
      )}
    </div>
  );
}


export const Messages = () => {
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const {
    markConversationRead, markChannelRead, unreadByConversation,
    channelUnreadByChannel, refreshUnreadCount,
  } = useUnreadMessages();
  const messagesEndRef = useRef(null);

  // Tab: 'channels' | 'dms'
  const [activeTab, setActiveTab] = useState('channels');

  // Channels state
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelMessages, setChannelMessages] = useState([]);
  const [channelMsgLoading, setChannelMsgLoading] = useState(false);
  const [newChannelMsg, setNewChannelMsg] = useState('');
  const [sendingChannelMsg, setSendingChannelMsg] = useState(false);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

  // Image upload
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // { file, previewUrl }
  const [isDragOver, setIsDragOver] = useState(false);

  // DM state (preserved from legacy)
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newConvDialogOpen, setNewConvDialogOpen] = useState(false);
  const [dmUsers, setDmUsers] = useState([]);
  const [vans, setVans] = useState([]);
  const [creatingConv, setCreatingConv] = useState(false);

  const pollRef = useRef(null);

  // AI Actions state
  const [aiSystemUser, setAiSystemUser] = useState(null); // { ai_user_id, display_name, enabled }
  const [aiTyping, setAiTyping] = useState(false);
  const aiPollRef = useRef(null);
  const aiPollCountRef = useRef(0);
  const [channelProposals, setChannelProposals] = useState({}); // { message_id: [proposals] }

  // Fetch proposals for the current channel
  const fetchChannelProposals = useCallback(async (chId) => {
    try {
      const res = await aiProposalsAPI.list({ channel_id: chId });
      const byMsg = {};
      for (const p of (res.data || [])) {
        if (p.message_id) {
          if (!byMsg[p.message_id]) byMsg[p.message_id] = [];
          byMsg[p.message_id].push(p);
        }
      }
      setChannelProposals(byMsg);
    } catch { setChannelProposals({}); }
  }, []);

  // ── Fetch channels ──
  const fetchChannels = useCallback(async () => {
    try {
      const res = await channelsAPI.list();
      setChannels(res.data);
    } catch (err) {
      console.error('Failed to load channels:', err);
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  // ── Fetch DM conversations ──
  const fetchConversations = useCallback(async () => {
    try {
      const res = await messagingAPI.listConversations();
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch channel messages ──
  const fetchChannelMessages = useCallback(async (chId) => {
    if (!chId) return;
    try {
      const res = await channelsAPI.listMessages(chId);
      setChannelMessages(res.data);
      // Also fetch proposals for this channel (non-blocking)
      fetchChannelProposals(chId);
    } catch (err) {
      console.error('Failed to load channel messages:', err);
    } finally {
      setChannelMsgLoading(false);
    }
  }, [fetchChannelProposals]);

  // ── Fetch DM messages ──
  const fetchMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const res = await messagingAPI.getMessages(convId);
      setMessages(res.data);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchChannels();
    fetchConversations();
    const fetchMeta = async () => {
      try {
        const [uRes, vRes] = await Promise.all([usersAPI.list(), vansAPI.list()]);
        setDmUsers((uRes.data || []).filter(u => u.id !== user.id && u.is_active));
        setVans((vRes.data || []).filter(v => v.is_active));
      } catch {}
    };
    fetchMeta();
    // Fetch AI system user info
    aiActionsAPI.getSystemUser().then(res => setAiSystemUser(res.data)).catch(() => {});
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (aiPollRef.current) clearInterval(aiPollRef.current);
    };
  }, [fetchChannels, fetchConversations, user?.id]);

  // Poll selected channel
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (selectedChannel) {
      setChannelMsgLoading(true);
      fetchChannelMessages(selectedChannel.id);
      markChannelRead(selectedChannel.id);
      pollRef.current = setInterval(() => {
        fetchChannelMessages(selectedChannel.id);
        markChannelRead(selectedChannel.id);
      }, 10000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedChannel, fetchChannelMessages, markChannelRead]);

  // Poll selected DM
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (selectedConversation) {
      setMessagesLoading(true);
      fetchMessages(selectedConversation.id);
      markConversationRead(selectedConversation.id);
      pollRef.current = setInterval(() => {
        fetchMessages(selectedConversation.id);
        markConversationRead(selectedConversation.id);
      }, 10000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedConversation, fetchMessages, markConversationRead]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [channelMessages, messages, aiTyping]);

  // ── Channel actions ──
  const handleSendChannelMsg = async () => {
    const hasText = newChannelMsg.trim().length > 0;
    const hasFile = !!pendingFile;
    if (!hasText && !hasFile) return;
    if (!selectedChannel) return;

    // Check if message mentions the AI system user
    const mentionsAI = aiSystemUser?.ai_user_id && hasText &&
      newChannelMsg.includes(`{{mention:user:${aiSystemUser.ai_user_id}:`);

    setSendingChannelMsg(true);
    try {
      const body = hasText ? newChannelMsg.trim() : `[Image: ${pendingFile.file.name}]`;
      const msgRes = await channelsAPI.sendMessage(selectedChannel.id, body);

      if (hasFile) {
        setUploadingImage(true);
        const resizedFile = await clientResizeImage(pendingFile.file, 1600);
        await channelsAPI.uploadAttachment(selectedChannel.id, msgRes.data.id, resizedFile);
        clearPendingFile();
        setUploadingImage(false);
      }

      setNewChannelMsg('');
      await fetchChannelMessages(selectedChannel.id);
      fetchChannels();

      // Start polling for AI response if AI was mentioned
      if (mentionsAI) {
        setAiTyping(true);
        aiPollCountRef.current = 0;
        if (aiPollRef.current) clearInterval(aiPollRef.current);
        const chId = selectedChannel.id;
        aiPollRef.current = setInterval(async () => {
          aiPollCountRef.current += 1;
          try {
            const res = await channelsAPI.listMessages(chId);
            const msgs = res.data || [];
            const hasAiReply = msgs.some(m => m.is_ai_response && m.created_at > msgRes.data.created_at);
            if (hasAiReply || aiPollCountRef.current > 30) {
              clearInterval(aiPollRef.current);
              aiPollRef.current = null;
              setAiTyping(false);
              setChannelMessages(msgs);
              // Refresh proposals (AI may have generated action cards)
              fetchChannelProposals(chId);
            }
          } catch {
            // Ignore poll errors
          }
        }, 2000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
      setUploadingImage(false);
    } finally {
      setSendingChannelMsg(false);
    }
  };

  const handleJoinChannel = async (ch) => {
    try {
      await channelsAPI.join(ch.id);
      toast.success(`Joined #${ch.name}`);
      fetchChannels();
      selectChannel({ ...ch, is_member: true, notify_mode: 'all' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join channel');
    }
  };

  const handleNotifyMode = async (mode) => {
    if (!selectedChannel) return;
    try {
      await channelsAPI.updateNotifyMode(selectedChannel.id, mode);
      const labels = { all: 'All messages', mentions: 'Mentions only', muted: 'Muted' };
      toast.success(`Notifications: ${labels[mode]}`);
      setSelectedChannel(prev => ({ ...prev, notify_mode: mode }));
      setChannels(prev => prev.map(ch => ch.id === selectedChannel.id ? { ...ch, notify_mode: mode } : ch));
      refreshUnreadCount();
    } catch (err) {
      toast.error('Failed to update notification preference');
    }
  };

  // ── File validation & staging ──
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_SIZE = 10 * 1024 * 1024;

  const stageFile = useCallback((file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are supported');
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('Image must be under 10 MB');
      return;
    }
    // Revoke previous preview URL if any
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    const previewUrl = URL.createObjectURL(file);
    setPendingFile({ file, previewUrl });
  }, [pendingFile]);

  const clearPendingFile = useCallback(() => {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    setPendingFile(null);
  }, [pendingFile]);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => { if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl); };
  }, [pendingFile]);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChannel) return;
    e.target.value = '';
    stageFile(file);
  };

  // ── Drag & Drop handlers ──
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget.contains(e.relatedTarget)) return; setIsDragOver(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    if (!selectedChannel?.is_member) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) stageFile(file);
  };

  // ── Clipboard paste handler (from MentionComposer) ──
  const handlePasteImage = useCallback((file) => {
    if (!selectedChannel?.is_member) return;
    stageFile(file);
  }, [selectedChannel, stageFile]);

  // ── DM actions (preserved) ──
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    setSendingMessage(true);
    try {
      const res = await messagingAPI.sendMessage(selectedConversation.id, newMessage.trim());
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      fetchConversations();
      markConversationRead(selectedConversation.id);
    } catch { toast.error('Failed to send message'); }
    finally { setSendingMessage(false); }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!isOwner || !window.confirm('Delete this message?')) return;
    try {
      await messagingAPI.deleteMessage(messageId);
      fetchMessages(selectedConversation.id);
      toast.success('Message deleted');
    } catch { toast.error('Failed to delete message'); }
  };

  const handleDeleteChannelMsg = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await channelsAPI.deleteMessage(selectedChannel.id, messageId);
      fetchChannelMessages(selectedChannel.id);
      toast.success('Message deleted');
    } catch { toast.error('Failed to delete message'); }
  };

  const handleReaction = async (messageId, emoji) => {
    if (!selectedChannel) return;
    try {
      const res = await channelsAPI.toggleReaction(selectedChannel.id, messageId, emoji);
      // Optimistic update: modify the reactions array in-place
      setChannelMessages(prev => prev.map(msg => {
        if (msg.id !== messageId) return msg;
        const reactions = [...(msg.reactions || [])];
        const idx = reactions.findIndex(r => r.emoji === emoji);
        if (res.data.action === 'added') {
          if (idx >= 0) {
            reactions[idx] = { ...reactions[idx], count: reactions[idx].count + 1, reacted: true };
          } else {
            reactions.push({ emoji, count: 1, reacted: true });
          }
        } else {
          if (idx >= 0) {
            if (reactions[idx].count <= 1) {
              reactions.splice(idx, 1);
            } else {
              reactions[idx] = { ...reactions[idx], count: reactions[idx].count - 1, reacted: false };
            }
          }
        }
        return { ...msg, reactions };
      }));
    } catch {
      toast.error('Failed to react');
    }
  };

  const handleCreateDM = async (targetUserId) => {
    setCreatingConv(true);
    try {
      const res = await messagingAPI.createDM(targetUserId);
      setConversations(prev => prev.find(c => c.id === res.data.id) ? prev : [res.data, ...prev]);
      setSelectedConversation(res.data);
      setSelectedChannel(null);
      setActiveTab('dms');
      setNewConvDialogOpen(false);
    } catch { toast.error('Failed to create conversation'); }
    finally { setCreatingConv(false); }
  };

  const handleOpenVehicleThread = async (vanId) => {
    setCreatingConv(true);
    try {
      const res = await messagingAPI.getVehicleThread(vanId);
      setConversations(prev => prev.find(c => c.id === res.data.id) ? prev : [res.data, ...prev]);
      setSelectedConversation(res.data);
      setSelectedChannel(null);
      setActiveTab('dms');
      setNewConvDialogOpen(false);
    } catch { toast.error('Failed to open vehicle thread'); }
    finally { setCreatingConv(false); }
  };

  // ── Helpers ──
  const getConversationTitle = (conv) => {
    if (conv.type === 'vehicle') return conv.vehicle_name || 'Vehicle Thread';
    const other = conv.members?.find(m => m.user_id !== user.id);
    return other?.user_name || 'Direct Message';
  };

  const getConversationIcon = (conv) => {
    if (conv.type === 'vehicle') return <Truck className="w-4 h-4 text-primary" />;
    return <User className="w-4 h-4 text-primary" />;
  };

  const selectChannel = (ch) => {
    setSelectedChannel(ch);
    setSelectedConversation(null);
    clearPendingFile();
    // Clear AI polling when switching channels
    if (aiPollRef.current) { clearInterval(aiPollRef.current); aiPollRef.current = null; }
    setAiTyping(false);
  };

  const selectDM = (conv) => {
    setSelectedConversation(conv);
    setSelectedChannel(null);
    clearPendingFile();
  };

  const deselectAll = () => {
    setSelectedChannel(null);
    setSelectedConversation(null);
    clearPendingFile();
  };

  const isViewingMessages = selectedChannel || selectedConversation;

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-8rem)] lg:h-[calc(100vh-8rem)]">
        {/* Header */}
        <div className={`flex items-center justify-between mb-4 ${isViewingMessages ? 'hidden lg:flex' : 'flex'}`}>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground">Messages</h1>
            <p className="text-muted-foreground mt-1 text-sm">Team communication</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCreateChannelOpen(true)} className="hidden lg:inline-flex" data-testid="create-channel-btn">
              <Hash className="w-4 h-4 mr-2" />New Channel
            </Button>
            <Button onClick={() => setNewConvDialogOpen(true)} className="hidden lg:inline-flex" data-testid="new-dm-btn">
              <Plus className="w-4 h-4 mr-2" />New DM
            </Button>
          </div>
        </div>

        {/* Main layout */}
        <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0 overflow-hidden">

          {/* ── LEFT SIDEBAR ── */}
          <div className={`w-full lg:w-80 shrink-0 flex flex-col gap-3 ${isViewingMessages ? 'hidden lg:flex' : 'flex'}`}>
          <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b shrink-0">
              <button
                className={`flex-1 text-sm font-medium py-3 text-center border-b-2 transition-colors ${
                  activeTab === 'channels' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('channels')}
                data-testid="tab-channels"
              >
                <Hash className="w-4 h-4 inline mr-1" />Channels
              </button>
              <button
                className={`flex-1 text-sm font-medium py-3 text-center border-b-2 transition-colors ${
                  activeTab === 'dms' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('dms')}
                data-testid="tab-dms"
              >
                <MessageSquare className="w-4 h-4 inline mr-1" />Direct
              </button>
            </div>

            <CardContent className="flex-1 overflow-y-auto p-0">
              {activeTab === 'channels' ? (
                /* ── Channels List ── */
                channelsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : channels.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Hash className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No channels yet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setCreateChannelOpen(true)}>
                      Create Channel
                    </Button>
                  </div>
                ) : (
                  <ChannelSidebarList
                    channels={channels}
                    selectedChannel={selectedChannel}
                    channelUnreadByChannel={channelUnreadByChannel}
                    onSelect={selectChannel}
                    onJoin={handleJoinChannel}
                  />
                )
              ) : (
                /* ── DMs List ── */
                loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : conversations.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No conversations yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {conversations.map(conv => {
                      const unread = unreadByConversation[conv.id] || 0;
                      return (
                        <div
                          key={conv.id}
                          className={`px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 ${
                            selectedConversation?.id === conv.id ? 'bg-muted' : ''
                          } ${unread > 0 ? 'bg-primary/5' : ''}`}
                          onClick={() => selectDM(conv)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 relative">
                              {getConversationIcon(conv)}
                              {unread > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-destructive text-[10px] font-bold text-white rounded-full flex items-center justify-center px-1">
                                  {unread > 9 ? '9+' : unread}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${unread > 0 ? 'font-bold' : 'font-semibold'}`}>{getConversationTitle(conv)}</p>
                              {conv.last_message && (
                                <p className={`text-xs truncate ${unread > 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                                  {conv.last_message}
                                </p>
                              )}
                            </div>
                            {conv.last_message_at && (
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {formatLocalTime(conv.last_message_at, 'MMM d')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </CardContent>
          </Card>
          {/* AI Usage Dashboard */}
          <AIUsageDashboard />
          </div>

          {/* ── MAIN PANEL ── */}
          <Card className={`flex-1 flex flex-col min-w-0 ${isViewingMessages ? 'flex' : 'hidden lg:flex'}`}>

            {!isViewingMessages ? (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-lg font-semibold">Select a channel or conversation</p>
                  <p className="text-sm text-muted-foreground mt-1">Choose from the sidebar or create new</p>
                </div>
              </CardContent>
            ) : selectedChannel ? (
              /* ── CHANNEL VIEW ── */
              <>
                <CardHeader className="pb-3 border-b shrink-0">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={deselectAll} data-testid="back-to-list-btn">
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center shrink-0">
                      {selectedChannel.is_private ? <Lock className="w-5 h-5 text-primary" /> : <Hash className="w-5 h-5 text-primary" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg truncate" data-testid="channel-header-name">{selectedChannel.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{selectedChannel.member_count} member{selectedChannel.member_count !== 1 ? 's' : ''}</p>
                    </div>
                    {selectedChannel.is_member && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" title="Notification preferences" data-testid="channel-notify-btn">
                            {selectedChannel.notify_mode === 'muted' ? <BellOff className="w-5 h-5 text-muted-foreground" />
                              : selectedChannel.notify_mode === 'mentions' ? <Bell className="w-5 h-5 text-amber-500" />
                              : <Bell className="w-5 h-5" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" data-testid="channel-notify-menu">
                          <DropdownMenuLabel className="text-xs">Notifications</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleNotifyMode('all')}
                            className={selectedChannel.notify_mode === 'all' || !selectedChannel.notify_mode ? 'bg-accent' : ''}
                            data-testid="notify-mode-all"
                          >
                            <BellRing className="w-4 h-4 mr-2" />
                            <div>
                              <p className="font-medium text-sm">All messages</p>
                              <p className="text-xs text-muted-foreground">Get notified for every message</p>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleNotifyMode('mentions')}
                            className={selectedChannel.notify_mode === 'mentions' ? 'bg-accent' : ''}
                            data-testid="notify-mode-mentions"
                          >
                            <Bell className="w-4 h-4 mr-2" />
                            <div>
                              <p className="font-medium text-sm">Mentions only</p>
                              <p className="text-xs text-muted-foreground">Only when someone @mentions you</p>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleNotifyMode('muted')}
                            className={selectedChannel.notify_mode === 'muted' ? 'bg-accent' : ''}
                            data-testid="notify-mode-muted"
                          >
                            <BellOff className="w-4 h-4 mr-2" />
                            <div>
                              <p className="font-medium text-sm">Muted</p>
                              <p className="text-xs text-muted-foreground">No badge — @mentions still alert</p>
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSettingsDrawerOpen(true)}
                      title="Channel settings"
                      data-testid="channel-settings-btn"
                    >
                      <Settings className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>

                <CardContent
                  className={`flex-1 overflow-y-auto p-4 space-y-3 relative ${isDragOver ? 'ring-2 ring-primary ring-inset' : ''}`}
                  data-testid="channel-messages-area"
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {isDragOver && (
                    <div className="absolute inset-0 bg-primary/5 backdrop-blur-[1px] z-10 flex items-center justify-center pointer-events-none rounded-lg">
                      <div className="flex flex-col items-center gap-2 text-primary">
                        <Upload className="w-10 h-10" />
                        <p className="text-sm font-semibold">Drop image to upload</p>
                      </div>
                    </div>
                  )}
                  {channelMsgLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : channelMessages.length === 0 ? (
                    <div className="text-center py-8">
                      <Hash className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    channelMessages.map(msg => {
                      const isOwn = msg.sender_user_id === user.id;
                      const isAI = msg.is_ai_response;
                      return (
                        <div key={msg.id} className={`flex ${isAI ? 'justify-start' : isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`channel-msg-${msg.id}`}>
                          <div className={`max-w-[85%] lg:max-w-[75%]`}>
                            {/* Sender name with AI badge */}
                            {isAI ? (
                              <div className="flex items-center gap-1.5 mb-1" data-testid={`ai-msg-header-${msg.id}`}>
                                <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center">
                                  <Bot className="w-3 h-3 text-emerald-600" />
                                </div>
                                <span className="text-xs font-semibold text-emerald-700">StockBot</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">AI</span>
                              </div>
                            ) : !isOwn ? (
                              <p className="text-xs font-medium text-muted-foreground mb-1">{msg.sender_name}</p>
                            ) : null}

                            <div className={`px-4 py-2 ${
                              msg.is_deleted ? 'rounded-2xl bg-muted/50 text-muted-foreground italic'
                                : isAI ? 'rounded-xl bg-emerald-50/80 text-foreground border border-emerald-200/60'
                                : isOwn ? 'rounded-2xl bg-primary text-primary-foreground'
                                : 'rounded-2xl bg-muted text-foreground'
                            }`}>
                              {msg.is_deleted ? (
                                <p className="text-sm">Message deleted</p>
                              ) : (
                                <div className={`text-sm break-words leading-relaxed ${isAI ? 'ai-response-body whitespace-pre-wrap' : ''}`}>
                                  <RenderMessageBody bodyRaw={msg.body_raw} mentions={msg.mentions} navigate={navigate} isOwn={isOwn} />
                                </div>
                              )}
                            </div>
                            {/* Attachments */}
                            {msg.attachments?.map(att => (
                              <ImageAttachment key={att.id} att={att} />
                            ))}
                            {/* AI Action Proposal Cards */}
                            {isAI && channelProposals[msg.id]?.map(proposal => (
                              <AIActionCard
                                key={proposal.id}
                                proposal={proposal}
                                onUpdate={(updated) => {
                                  setChannelProposals(prev => {
                                    const msgProposals = (prev[msg.id] || []).map(p =>
                                      p.id === updated.id ? updated : p
                                    );
                                    return { ...prev, [msg.id]: msgProposals };
                                  });
                                }}
                              />
                            ))}
                            {/* Reaction pills */}
                            {!msg.is_deleted && (
                              <ReactionPills
                                reactions={msg.reactions}
                                onToggle={(emoji) => handleReaction(msg.id, emoji)}
                                msgId={msg.id}
                              />
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatLocalTime(msg.created_at, 'h:mm a')}
                              </p>
                              {!msg.is_deleted && selectedChannel.is_member && (
                                <EmojiPicker
                                  onSelect={(emoji) => handleReaction(msg.id, emoji)}
                                  msgId={msg.id}
                                  userId={user.id}
                                />
                              )}
                              {(isOwn || isOwner) && !msg.is_deleted && (
                                <button
                                  onClick={() => handleDeleteChannelMsg(msg.id)}
                                  className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                                  data-testid={`delete-channel-msg-${msg.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {/* AI Typing Indicator */}
                  {aiTyping && (
                    <div className="flex justify-start" data-testid="ai-typing-indicator">
                      <div className="max-w-[85%] lg:max-w-[75%]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center">
                            <Bot className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-xs font-semibold text-emerald-700">StockBot</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">AI</span>
                        </div>
                        <div className="rounded-2xl px-4 py-3 bg-emerald-50 border border-emerald-200/60">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                            <span className="text-sm text-emerald-700">Thinking...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>

                {/* Channel Composer or Join bar */}
                {selectedChannel.is_member ? (
                  <div className="border-t shrink-0 bg-background">
                    {/* Pending file preview */}
                    {pendingFile && (
                      <div className="px-4 pt-3 pb-0" data-testid="pending-file-preview">
                        <div className="inline-flex items-start gap-2 bg-muted rounded-lg p-2 relative group">
                          <img
                            src={pendingFile.previewUrl}
                            alt="Upload preview"
                            className="h-16 w-auto rounded object-cover"
                            data-testid="pending-file-thumb"
                          />
                          <div className="flex flex-col gap-0.5 min-w-0 pr-5">
                            <p className="text-xs font-medium truncate max-w-[160px]">{pendingFile.file.name}</p>
                            <p className="text-[10px] text-muted-foreground">{(pendingFile.file.size / 1024).toFixed(0)} KB</p>
                          </div>
                          <button
                            onClick={clearPendingFile}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center hover:bg-destructive/80 transition-colors shadow-sm"
                            data-testid="pending-file-remove"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="p-4 flex gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleImageUpload}
                        data-testid="channel-image-input"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                        title="Upload image"
                        data-testid="channel-image-upload-btn"
                      >
                        {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                      </Button>
                      <MentionComposer
                        value={newChannelMsg}
                        onChange={setNewChannelMsg}
                        onSubmit={handleSendChannelMsg}
                        onPasteImage={handlePasteImage}
                        placeholder="Message #channel... Use @ to mention"
                        disabled={sendingChannelMsg}
                      />
                      <Button
                        onClick={handleSendChannelMsg}
                        disabled={sendingChannelMsg || (!newChannelMsg.trim() && !pendingFile)}
                        data-testid="channel-send-btn"
                      >
                        {sendingChannelMsg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-t shrink-0 bg-muted/30">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-muted-foreground">Join this channel to start posting</p>
                      <Button size="sm" onClick={() => handleJoinChannel(selectedChannel)} data-testid="join-channel-footer-btn">
                        Join #{selectedChannel.name}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : selectedConversation ? (
              /* ── DM VIEW (preserved) ── */
              <>
                <CardHeader className="pb-3 border-b shrink-0">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={deselectAll} data-testid="back-to-list-btn">
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      {getConversationIcon(selectedConversation)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg truncate">{getConversationTitle(selectedConversation)}</CardTitle>
                      {selectedConversation.type === 'vehicle' && (
                        <p className="text-sm text-muted-foreground">{selectedConversation.members?.length || 0} members</p>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messagesLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-8"><p className="text-muted-foreground">No messages yet. Start the conversation!</p></div>
                  ) : (
                    messages.map(msg => {
                      const isOwnMessage = msg.user_id === user.id;
                      return (
                        <div key={msg.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] lg:max-w-[75%]`}>
                            {!isOwnMessage && <p className="text-xs font-medium text-muted-foreground mb-1">{msg.user_name}</p>}
                            <div className={`rounded-2xl px-4 py-2 ${
                              msg.is_deleted ? 'bg-muted/50 text-muted-foreground italic'
                                : isOwnMessage ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                            }`}>
                              <p className="text-sm break-words">{msg.body}</p>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />{formatLocalTime(msg.created_at, 'h:mm a')}
                              </p>
                              {isOwner && !msg.is_deleted && (
                                <button onClick={() => handleDeleteMessage(msg.id)} className="text-xs text-muted-foreground hover:text-destructive transition-colors">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            {msg.is_deleted && msg.deleted_by_name && (
                              <p className="text-xs text-muted-foreground mt-1">Removed by {msg.deleted_by_name}</p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </CardContent>

                <div className="p-4 border-t shrink-0 bg-background">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input placeholder="Type a message..." value={newMessage} onChange={e => setNewMessage(e.target.value)} className="flex-1" />
                    <Button type="submit" disabled={sendingMessage || !newMessage.trim()}>
                      {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </form>
                </div>
              </>
            ) : null}
          </Card>
        </div>
      </div>

      {/* Create Channel Modal */}
      <ChannelCreateModal
        open={createChannelOpen}
        onOpenChange={setCreateChannelOpen}
        onCreated={(ch) => {
          setChannels(prev => [ch, ...prev]);
          selectChannel(ch);
          setActiveTab('channels');
        }}
      />

      {/* Channel Settings Drawer */}
      <ChannelSettingsDrawer
        open={settingsDrawerOpen}
        onOpenChange={setSettingsDrawerOpen}
        channel={selectedChannel}
        onUpdated={(updated) => {
          setSelectedChannel(prev => ({ ...prev, ...updated }));
          setChannels(prev => prev.map(ch => ch.id === updated.id ? { ...ch, ...updated } : ch));
        }}
        onArchived={(chId) => {
          setChannels(prev => prev.filter(ch => ch.id !== chId));
          setSelectedChannel(null);
        }}
      />

      {/* New DM Dialog (preserved) */}
      <Dialog open={newConvDialogOpen} onOpenChange={setNewConvDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">New Conversation</DialogTitle>
            <DialogDescription>Start a direct message or open a vehicle thread</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />Direct Message
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {dmUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No other users</p>
                ) : dmUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 border rounded-lg hover:border-primary/50 cursor-pointer transition-colors"
                    onClick={() => !creatingConv && handleCreateDM(u.id)}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center"><User className="w-4 h-4 text-primary" /></div>
                      <div><p className="font-medium text-sm">{u.name}</p><p className="text-xs text-muted-foreground capitalize">{u.role}</p></div>
                    </div>
                    {creatingConv && <Loader2 className="w-4 h-4 animate-spin" />}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
                <Truck className="w-4 h-4" />Vehicle Threads
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {vans.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No vehicles</p>
                ) : vans.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-3 border rounded-lg hover:border-primary/50 cursor-pointer transition-colors"
                    onClick={() => !creatingConv && handleOpenVehicleThread(v.id)}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center"><Truck className="w-4 h-4 text-primary" /></div>
                      <div><p className="font-medium text-sm">{v.name}</p><p className="text-xs text-muted-foreground">{v.van_number || 'No number'}</p></div>
                    </div>
                    {creatingConv && <Loader2 className="w-4 h-4 animate-spin" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile FAB */}
      {!isViewingMessages && (
        <div className="lg:hidden fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-4 z-50 flex flex-col gap-2">
          <button
            className="w-12 h-12 bg-secondary text-secondary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-secondary/90 transition-all active:scale-95"
            onClick={() => setCreateChannelOpen(true)}
            data-testid="create-channel-fab"
          >
            <Hash className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <button
            className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
            onClick={() => setNewConvDialogOpen(true)}
            data-testid="new-conversation-fab"
          >
            <Plus className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      )}
    </Layout>
  );
};


// ── Client-side image resize ──
async function clientResizeImage(file, maxDim) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > maxDim) {
        const ratio = maxDim / longest;
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        resolve(new File([blob], file.name, { type: 'image/webp' }));
      }, 'image/webp', 0.82);
    };
    img.onerror = () => resolve(file); // fallback: send original
    img.src = URL.createObjectURL(file);
  });
}
